import ApplicationService from 'src/api/application';
import filteringHelper from 'src/services/filteringHelper';
import Vue from 'vue';

const JOBS_STORAGE_KEY = 'export_jobs';

function saveJobs(jobs) {
  localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(jobs));
}

const state = {
  // Per-job tracking (multi-job model)
  jobs: [],

  // Visualization-collection coordination state (used by ExportVisualizationCollector)
  messages: '',
  formData: null,
  categories: {
    publication: [],
    patent: [],
  },
  source: [],

  // Legacy fields kept for safety — no longer drive UI
  status: null,
  downloadUrl: null,
  error: null,
};

const getters = {
  // Jobs array
  jobs: (state) => state.jobs,

  // Badge status for ScanHeader: null | 'pending' | 'ready' | 'error'
  badgeStatus: (state, _getters, _rootState, rootGetters) => {
    const currentScanId = rootGetters['scans/getScanDetails']?.version_id;
    const scanJobs = currentScanId
      ? state.jobs.filter((j) => j.scanId === currentScanId)
      : state.jobs;
    if (!scanJobs.length) return null;
    if (scanJobs.some((j) => j.error || j.status === 'ERROR')) return 'error';
    if (scanJobs.some((j) => j.status === 'COMPLETE')) return 'ready';
    if (scanJobs.some((j) => j.status === 'PENDING')) return 'pending';
    return null;
  },

  // Visualization-collection state
  categories: (state) => state.categories,
  source: (state) => state.source,
  messages: (state) => state.messages,

  // Legacy getters (kept for any remaining references)
  downloadUrl: (state) =>
    state.jobs.find((j) => j.downloadUrl)?.downloadUrl || null,
  error: (state) => state.jobs.some((j) => j.error),
  status: (state) => state.jobs[0]?.status || null,
};

const actions = {
  buildFormData({ commit, rootGetters, dispatch }, payload) {
    if (payload.options) {
      dispatch('clearMessages');
      dispatch('messageHandler', { message: 'Initializing export...' });
      commit('updateFormData', null);
      commit('updateSource', payload.source);

      const { query, version_id, name, created_date, query_elements } =
        rootGetters['scans/getScanDetails'];
      const { product } = rootGetters['app/appConfig'];
      const scanType = rootGetters['scans/scanType'];

      let formdata = {};
      formdata.source = payload.source;
      formdata.options = payload.options;
      formdata.zip_name = payload.zip_name;
      formdata.scan_id = version_id;
      formdata.scan_name = name;
      formdata.scan_type = scanType;
      formdata.scan_search_query = query_elements.semantic.length
        ? query_elements.semantic[0]
        : query_elements.boolean.length
        ? query_elements.boolean[0].value
        : name;
      formdata.scan_date = created_date ? created_date : null;
      formdata.query_type = query_elements.semantic.length
        ? 'Semantic'
        : 'Keyword';
      formdata.productName = product.name;

      if (payload.publication) {
        commit('updateCategories', {
          source: 'publication',
          data: payload.publication.categories,
        });
        const publicationFilters =
          rootGetters['filters/currentFiltering']['publication'] || null;
        payload.publication.filters = publicationFilters;
        payload.publication.query = query;

        if (scanType !== 'semantic') {
          if (publicationFilters && publicationFilters.search_within_results) {
            payload.publication.query = filteringHelper.filterResultsText(
              query,
              publicationFilters.search_within_results
            );
          }
        } else {
          if (publicationFilters && (publicationFilters.positive || publicationFilters.negative)) {
            const positive = publicationFilters.positive || [];
            const negative = publicationFilters.negative || [];
            if (positive.length || negative.length) {
              payload.publication.query = filteringHelper.filterSemantic(
                query,
                { positive, negative }
              );
            }
          }
        }

        payload.publication.leadersBy = payload.publication.categories.includes(
          'leaders'
        )
          ? rootGetters['leaders/leadersBy'].publication
          : null;

        formdata.publication = payload.publication;
      }

      if (payload.patent) {
        commit('updateCategories', {
          source: 'patent',
          data: payload.patent.categories,
        });
        const patentFilters =
          rootGetters['filters/currentFiltering']['patent'] || null;
        payload.patent.filters = patentFilters;
        payload.patent.query = query;

        if (scanType !== 'semantic') {
          if (patentFilters && patentFilters.search_within_results) {
            payload.patent.query = filteringHelper.filterResultsText(
              query,
              patentFilters.search_within_results
            );
          }
        } else {
          if (patentFilters && (patentFilters.positive || patentFilters.negative)) {
            const positive = patentFilters.positive || [];
            const negative = patentFilters.negative || [];
            if (positive.length || negative.length) {
              payload.patent.query = filteringHelper.filterSemantic(query, {
                positive,
                negative,
              });
            }
          }
        }

        payload.patent.leadersBy = payload.patent.categories.includes('leaders')
          ? rootGetters['leaders/leadersBy'].patent
          : null;

        formdata.patent = payload.patent;
      }

      commit('updateFormData', formdata);

      const categoryQueue = payload.source
        .map((source) => {
          return payload[source].categories.filter(
            (category) => category !== 'documents'
          );
        })
        .flat();

      if (!categoryQueue.length) {
        dispatch('initializeExport');
      }
    } else {
      state.source.forEach((source) => {
        const pagination = rootGetters['documents/getPagination'][source];
        const sourceData = JSON.parse(state.formData.get(source));
        sourceData.scan_doc_count = pagination.rowsNumber;
        payload[source] = sourceData;
      });
      commit('updateFormData', payload);
      dispatch('initializeExport');
    }
  },

  async initializeExport({ commit, rootGetters, dispatch, state }) {
    commit('updateCategories');
    const { version_id } = rootGetters['scans/getScanDetails'];

    // Extract display metadata from the current FormData before sending
    let zipName = 'Export';
    let options = [];
    let sources = [];
    let categories = {};
    try {
      zipName = state.formData.get('zip_name') || 'Export';
      options = JSON.parse(state.formData.get('options') || '[]');
      sources = JSON.parse(state.formData.get('source') || '[]');
      sources.forEach((src) => {
        try {
          const srcData = JSON.parse(state.formData.get(src) || '{}');
          categories[src] = srcData.categories || [];
        } catch {
          categories[src] = [];
        }
      });
    } catch {
      // FormData parse failed — proceed with defaults
    }

    // Add a placeholder job immediately so the UI shows it before the API call returns
    const placeholderId = `_init_${Date.now()}`;
    commit('addJob', {
      taskId: placeholderId,
      zipName,
      scanId: version_id,
      startedAt: new Date().toISOString(),
      status: 'PENDING',
      message: 'Initializing export...',
      downloadUrl: null,
      error: false,
      options,
      sources,
      categories,
      collectionProgress: null,
    });

    const applicationService = new ApplicationService();
    const response = await applicationService.initializeExport(state.formData);

    if (response && response.taskId) {
      commit('replaceJobId', { oldId: placeholderId, newId: response.taskId });
      dispatch('io/addTask', response.taskId, { root: true });

      let task_dict =
        JSON.parse(localStorage.getItem('task_dict') || '{}') || {};
      task_dict[version_id] = response.taskId;
      localStorage.setItem('task_dict', JSON.stringify(task_dict));
    } else {
      commit('updateJob', {
        taskId: placeholderId,
        changes: { error: true, status: 'ERROR', message: 'Something went wrong. Please try again.' },
      });
    }
  },

  messageHandler({ commit, dispatch }, payload) {
    if (payload) {
      // Update flat legacy state (for ExportVisualizationCollector messaging)
      if (payload.message) {
        commit('setMessage', payload.message);
      }
      if (payload.error && !payload.taskId) {
        commit('setError', payload.error);
      }

      // Update the matching job if we have a taskId
      if (payload.taskId) {
        const changes = {
          message: payload.message || '',
        };
        if (payload.data?.downloadUrl) {
          changes.downloadUrl = payload.data.downloadUrl;
          changes.status = 'COMPLETE';
        }
        if (payload.data?.collectionProgress) {
          changes.collectionProgress = payload.data.collectionProgress;
        }
        if (payload.error) {
          changes.error = true;
          changes.status = 'ERROR';
        }
        commit('updateJob', { taskId: payload.taskId, changes });

        if (payload.data?.downloadUrl || payload.error) {
          dispatch('io/removeTask', payload.taskId, { root: true });
        }
      }
    }
  },

  async pollPendingJobs({ commit, dispatch, state }) {
    const pendingJobs = state.jobs.filter((j) => j.status === 'PENDING');
    if (!pendingJobs.length) return;
    const applicationService = new ApplicationService();
    for (const job of pendingJobs) {
      const result = await applicationService.getExportStatus(job.taskId);
      if (!result || result.error) continue;
      const changes = {};
      if (result.message) changes.message = result.message;
      if (result.data?.collectionProgress) changes.collectionProgress = result.data.collectionProgress;
      if (result.status === 'COMPLETE' && result.data?.downloadUrl) {
        changes.downloadUrl = result.data.downloadUrl;
        changes.status = 'COMPLETE';
        commit('updateJob', { taskId: job.taskId, changes });
        dispatch('io/removeTask', job.taskId, { root: true });
      } else if (result.status === 'ERROR') {
        changes.error = true;
        changes.status = 'ERROR';
        commit('updateJob', { taskId: job.taskId, changes });
        dispatch('io/removeTask', job.taskId, { root: true });
      } else if (Object.keys(changes).length) {
        commit('updateJob', { taskId: job.taskId, changes });
      }
    }
  },

  async hydrateFromScan({ commit, state }, scanId) {
    const applicationService = new ApplicationService();
    const results = await applicationService.getExportsByScan(scanId);
    if (!results || results.error || !Array.isArray(results)) return;

    results.forEach((row) => {
      const existing = state.jobs.find((j) => j.taskId === row.taskId);
      if (existing) {
        const changes = { message: row.message, status: row.status };
        if (row.data?.downloadUrl) changes.downloadUrl = row.data.downloadUrl;
        commit('updateJob', { taskId: row.taskId, changes });
      } else {
        commit('addJob', {
          taskId: row.taskId,
          zipName: row.data?.zipName || 'Export',
          scanId: row.scan_id,
          startedAt: row.data?.startedAt || new Date().toISOString(),
          status: row.status,
          message: row.message,
          downloadUrl: row.data?.downloadUrl || null,
          error: row.status === 'ERROR',
          options: row.data?.options || [],
          sources: row.data?.sources || [],
          categories: row.data?.categories || {},
          collectionProgress: null,
        });
      }
    });
  },

  removeJob({ commit, dispatch }, taskId) {
    commit('removeJob', taskId);
    dispatch('io/removeTask', taskId, { root: true });
    const taskDict = JSON.parse(localStorage.getItem('task_dict') || '{}');
    const updated = Object.fromEntries(
      Object.entries(taskDict).filter(([, v]) => v !== taskId)
    );
    localStorage.setItem('task_dict', JSON.stringify(updated));
  },

  clearMessages({ commit }) {
    commit('clearMessages');
  },

  async hydrateFromStorage({ commit, dispatch }) {
    const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
    const now = Date.now();

    // Restore jobs from localStorage, dropping completed/errored jobs older than 24 hours
    try {
      const stored = JSON.parse(
        localStorage.getItem(JOBS_STORAGE_KEY) || '[]'
      );
      const live = stored.filter((j) => {
        const isFinished = j.status === 'COMPLETE' || j.status === 'ERROR' || j.error;
        const age = now - new Date(j.startedAt).getTime();
        return !(isFinished && age > TWENTY_FOUR_HOURS);
      });
      if (live.length !== stored.length) {
        saveJobs(live);
      }
      if (live.length) {
        commit('setJobs', live);
      }
    } catch {
      // Corrupt storage — ignore
    }

    // Re-query PENDING jobs from the DB to get their latest status
    const taskDict = JSON.parse(localStorage.getItem('task_dict') || '{}');
    const taskIds = Object.values(taskDict);
    if (!taskIds.length) return;

    const applicationService = new ApplicationService();
    for (const taskId of taskIds) {
      const result = await applicationService.getExportStatus(taskId);
      if (!result || result.error) continue;

      if (result.message) {
        commit('updateJob', {
          taskId,
          changes: { message: result.message },
        });
      }
      if (result.status === 'COMPLETE' && result.data && result.data.downloadUrl) {
        commit('updateJob', {
          taskId,
          changes: {
            downloadUrl: result.data.downloadUrl,
            status: 'COMPLETE',
          },
        });
        dispatch('io/removeTask', taskId, { root: true });
      } else if (result.status === 'ERROR') {
        commit('updateJob', {
          taskId,
          changes: { error: true, status: 'ERROR' },
        });
        dispatch('io/removeTask', taskId, { root: true });
      }
    }
  },
};

const mutations = {
  // Jobs mutations
  addJob(state, job) {
    state.jobs = [...state.jobs, job];
    saveJobs(state.jobs);
  },
  updateJob(state, { taskId, changes }) {
    const idx = state.jobs.findIndex((j) => j.taskId === taskId);
    if (idx !== -1) {
      Vue.set(state.jobs, idx, { ...state.jobs[idx], ...changes });
      saveJobs(state.jobs);
    }
  },
  removeJob(state, taskId) {
    state.jobs = state.jobs.filter((j) => j.taskId !== taskId);
    saveJobs(state.jobs);
  },
  replaceJobId(state, { oldId, newId }) {
    const idx = state.jobs.findIndex((j) => j.taskId === oldId);
    if (idx !== -1) {
      Vue.set(state.jobs, idx, { ...state.jobs[idx], taskId: newId });
      saveJobs(state.jobs);
    }
  },
  setJobs(state, jobs) {
    state.jobs = jobs;
  },

  // Legacy / visualization-coordination mutations
  setDownloadUrl(state, payload) {
    state.downloadUrl = payload;
  },
  setMessage(state, payload) {
    state.messages = payload;
  },
  clearMessages(state) {
    state.messages = '';
    state.downloadUrl = null;
    state.error = null;
    state.status = null;
  },
  setError(state, payload) {
    state.error = payload;
  },
  updateSource(state, payload) {
    state.source = payload;
  },
  updateCategories(state, payload) {
    if (!payload) {
      state.categories = {
        publication: [],
        patent: [],
      };
    } else {
      Vue.set(state.categories, payload.source, payload.data);
    }
  },
  updateFormData(state, payload) {
    if (!payload || !state.formData) state.formData = new FormData();
    if (payload) {
      for (const [key, value] of Object.entries(payload)) {
        let _value = null;
        if (Array.isArray(value)) {
          const val = value.map((item) => {
            if (typeof item === 'string') {
              return item.replace(/'/g, "''").replace(/\\/g, '');
            }
            return item;
          });
          _value = JSON.stringify(val);
        } else if (value instanceof File || typeof value === 'string') {
          _value = value;
        } else {
          _value = JSON.stringify(value, (_key, val) => {
            if (typeof val === 'string')
              return val.replace(/'/g, "''").replace(/\\/g, '');
            return val;
          });
        }
        state.formData.set(key, _value);
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
